import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import AudioPlayer from '@/components/audio/audio-player';
import Loading from '@/components/loading';
import { CHALLENGE, ChallengeTrack } from '@/lib/challenge-data';
import {
  useAffirmations,
  useChallengeProgress,
  useDailyReminder,
  useIntroHeard,
} from '@/lib/hooks/use-challenge';
import { downloadAffirmationsIcs } from '@/lib/challenge-calendar';
import { cn } from '@/lib/utils';
import { IS_HARTZORG } from '@/lib/constants';
import { useProfile } from '@/lib/providers';
import { AdminRole } from '@/types/custom';
import {
  Bell,
  BellOff,
  CalendarPlus,
  Check,
  ChevronDown,
  Info,
  Pause,
  Play,
} from 'lucide-react';
import { TrophyIcon } from '@/components/challenge/trophy-icon';

/**
 * MOCK CONTENT — 7-daagse challenge, first theme "Dankbaarheid".
 *
 * The texts and audio in src/lib/challenge-data.ts are placeholders. The
 * progress and completion tracking around them is real.
 *
 * Each section is a card with its own play button; the player itself lives
 * fixed at the bottom of the page, the way the natuurgeluiden catalogue does
 * it (see components/audio-catalog.tsx). Stopping clears the bar.
 *
 * The 13:00 reminder uses the browser's Notification API, which only fires
 * while the app is open. Reminders that arrive with the app closed need the
 * @capacitor/local-notifications plugin added to the native builds.
 */

// #00A700 is the green of the heart in the logo. It appears as a literal
// throughout because Tailwind only generates classes it can read in source.

/** One playable section: play/stop on the left, the tick on the right. */
function SectionHeading({
  title,
  track,
  playing,
  onPlayToggle,
  done,
  onToggleDone,
}: {
  title: string;
  track: ChallengeTrack;
  playing: boolean;
  onPlayToggle: () => void;
  done?: boolean;
  onToggleDone?: () => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <button
        onClick={onPlayToggle}
        aria-label={playing ? `Pauzeer ${title}` : `Speel ${title}`}
        className="flex-shrink-0 h-9 w-9 rounded-full bg-[#00A700] text-white flex items-center justify-center hover:bg-[#00A700]/90 transition-colors"
      >
        {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
      </button>

      <div className="min-w-0 flex-1">
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground truncate">
          {track.duration}
        </p>
      </div>

      {onToggleDone && (
        <Button
          variant={done ? 'default' : 'outline'}
          size="sm"
          onClick={onToggleDone}
          className={cn(
            'flex-shrink-0',
            done && 'bg-[#00A700] hover:bg-[#00A700]/90',
          )}
        >
          <Check className="h-4 w-4 sm:mr-1" />
          <span className="hidden sm:inline">
            {done ? 'Afgerond' : 'Markeer als af'}
          </span>
        </Button>
      )}
    </div>
  );
}

export default function ChallengePage() {
  const navigate = useNavigate();
  const { profile, loadingProfile } = useProfile();
  const isAdmin = profile?.role === AdminRole;

  const {
    progress,
    toggle,
    reset,
    completedDays,
    currentDay,
    completion,
    loading,
  } = useChallengeProgress(CHALLENGE.slug, CHALLENGE.days.length);

  const {
    heard: introHeard,
    markHeard,
    loading: introLoading,
  } = useIntroHeard(CHALLENGE.slug);
  // 'intro' is the information circle that sits before day 1
  const [selected, setSelected] = useState<'intro' | number>('intro');
  const [showCongrats, setShowCongrats] = useState(false);

  // The bar sits at the bottom from the moment the page opens, cued to the
  // introduction, the way the natuurgeluiden catalogue always shows a player.
  const [activeTrack, setActiveTrack] = useState<ChallengeTrack>(
    CHALLENGE.introSections[0].audio,
  );
  const [shouldPlay, setShouldPlay] = useState(false);
  const [shouldPause, setShouldPause] = useState(false);
  // which track is actually sounding, so a card can show pause instead of play
  const [playingId, setPlayingId] = useState<string | null>(null);

  // The player is fixed at the bottom, so the page reserves exactly as much
  // room as it takes up — measured rather than guessed, since it changes
  // height between phone and desktop.
  const playerRef = useRef<HTMLDivElement>(null);
  const [playerHeight, setPlayerHeight] = useState(0);

  // null until progress has loaded; then it records whether the challenge was
  // already finished when the page opened, so the dialog fires on the moment
  // of finishing rather than on every later visit
  const finishedOnArrival = useRef<boolean | null>(null);

  const selectedDay = typeof selected === 'number' ? selected : currentDay;
  const day =
    CHALLENGE.days.find((d) => d.day === selectedDay) ?? CHALLENGE.days[0];
  const dayProgress = progress[day.day] ?? { meditation: false };

  const { chosen, toggle: toggleAffirmation } = useAffirmations(
    CHALLENGE.slug,
    selectedDay,
  );
  const chosenLines = day.moment.groups
    .flatMap((group) => group.items)
    .filter((item) => chosen.has(item.id))
    .map((item) => item.text);

  const { state: reminderState, setReminder } = useDailyReminder(
    CHALLENGE.slug,
    CHALLENGE.reminderTime,
    chosenLines[0] ?? 'Even stilstaan bij waar je dankbaar voor bent.',
  );

  // The challenge is a Hartzorg feature, and while the content is still draft
  // it is admin-only — the menu entry is hidden too, but the route has to hold
  // the line for anyone who has the URL.
  useEffect(() => {
    if (loadingProfile) return;
    if (!IS_HARTZORG || !isAdmin) navigate('/', { replace: true });
  }, [isAdmin, loadingProfile, navigate]);

  // Jump to the day the user is on, once progress has loaded.
  useEffect(() => {
    if (loading) return;
    // Someone who hasn't ticked anything yet starts at the introduction;
    // anyone underway lands on the day they're on. Keyed on `loading` only, so
    // the view doesn't jump around as they tick boxes.
    const started = Object.keys(progress).length > 0;
    setSelected(started ? currentDay : 'intro');
  }, [loading]);

  useEffect(() => {
    if (loading) return;
    if (finishedOnArrival.current === null) {
      finishedOnArrival.current = !!completion;
      return;
    }
    if (completion && !finishedOnArrival.current) {
      finishedOnArrival.current = true;
      setShowCongrats(true);
    }
  }, [completion, loading]);

  const playTrack = (track: ChallengeTrack, onStart?: () => void) => {
    if (playingId === track.id) {
      setShouldPause(true); // pause without tearing the bar down
      return;
    }
    if (activeTrack.id !== track.id) setActiveTrack(track);
    setShouldPause(false);
    setShouldPlay(true);
    onStart?.();
  };

  useEffect(() => {
    const el = playerRef.current;
    if (!el) return;

    const measure = () => setPlayerHeight(el.offsetHeight);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // keyed on the loading flags so it runs once the ref is actually attached
  }, [loading, introLoading]);

  const isDone = (d: number) => !!progress[d]?.meditation;

  const pct = Math.round((completedDays / CHALLENGE.days.length) * 100);

  if (loading || introLoading || loadingProfile) return <Loading />;

  return (
    <div
      className="min-h-screen p-6 sm:p-8 space-y-6"
      style={{ paddingBottom: playerHeight + 24 }}
    >
      {/* Hero */}
      <div className="rounded-2xl border border-[#00A700]/25 bg-gradient-to-br from-[#00A700]/12 via-[#00A700]/5 to-transparent p-6 sm:p-8 space-y-4">
        <h2 className="text-3xl font-semibold">
          {CHALLENGE.days.length} dagen{' '}
          <span className="font-bold text-[#00A700]">{CHALLENGE.theme}</span>
        </h2>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">
              {completedDays} van {CHALLENGE.days.length} dagen afgerond
            </span>
            {completedDays > 0 && (
              <Button variant="ghost" size="sm" onClick={reset}>
                Opnieuw beginnen
              </Button>
            )}
          </div>
          <div className="h-2.5 w-full rounded-full bg-[#00A700]/15 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#00A700] transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Day picker, with the introduction sitting before day 1 */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setSelected('intro')}
          className={cn(
            'h-12 w-12 rounded-full border-2 flex items-center justify-center transition-colors',
            selected === 'intro'
              ? 'border-[#00A700] bg-[#00A700] text-white'
              : 'border-border text-muted-foreground hover:border-[#00A700]/50',
          )}
          aria-label="Introductie"
        >
          <Info className="h-5 w-5" />
        </button>

        <span className="mx-1 h-8 w-px bg-border" aria-hidden="true" />

        {CHALLENGE.days.map((d) => {
          const active = d.day === selected;
          const finished = isDone(d.day);
          return (
            <button
              key={d.day}
              onClick={() => setSelected(d.day)}
              className={cn(
                'h-12 w-12 rounded-full border-2 text-sm font-semibold flex items-center justify-center transition-colors',
                active && 'border-[#00A700] bg-[#00A700] text-white',
                !active && finished && 'border-[#00A700] text-[#00A700]',
                !active &&
                  !finished &&
                  'border-border text-muted-foreground hover:border-[#00A700]/50',
              )}
              aria-label={`Dag ${d.day}`}
            >
              {finished && !active ? <Check className="h-5 w-5" /> : d.day}
            </button>
          );
        })}
      </div>

      {/* The introduction, behind the information circle */}
      {selected === 'intro' &&
        CHALLENGE.introSections.map((section) => {
          const playing = playingId === section.audio.id;
          const listened = introHeard.has(section.id);

          return (
            <Collapsible
              key={section.id}
              defaultOpen={!introHeard.has(section.id)}
              className="space-y-3"
            >
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    playTrack(section.audio, () => markHeard(section.id))
                  }
                  aria-label={
                    playing
                      ? `Pauzeer ${section.title}`
                      : `Speel ${section.title}`
                  }
                  className="flex-shrink-0 h-9 w-9 rounded-full bg-[#00A700] text-white flex items-center justify-center hover:bg-[#00A700]/90 transition-colors"
                >
                  {playing ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5" />
                  )}
                </button>

                <CollapsibleTrigger className="group flex items-center gap-2 text-left min-w-0">
                  <h3 className="text-xl font-semibold truncate">
                    {section.title}
                  </h3>
                  {listened && (
                    <span className="flex items-center gap-1 text-sm text-[#00A700] flex-shrink-0">
                      <Check className="h-4 w-4" />
                      beluisterd
                    </span>
                  )}
                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
              </div>

              <CollapsibleContent className="space-y-3 max-w-3xl">
                {section.body.map((paragraph, i) =>
                  paragraph.heading ? (
                    <p key={i} className="font-semibold">
                      {paragraph.text}
                    </p>
                  ) : (
                    <p key={i} className="text-muted-foreground">
                      {paragraph.text}
                    </p>
                  ),
                )}
              </CollapsibleContent>
            </Collapsible>
          );
        })}

      {/* Reminder, at the foot of the information page */}
      {selected === 'intro' && (
        <div className="rounded-xl border border-dashed border-[#00A700]/40 bg-[#00A700]/[0.03] p-4 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-[#00A700]">
                {reminderState === 'on' ? (
                  <Bell className="h-4 w-4" />
                ) : (
                  <BellOff className="h-4 w-4" />
                )}
                <span>Een klein moment van aandacht</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Een melding om {CHALLENGE.reminderTime} met de zin die je voor
                die dag hebt gekozen.
              </p>
            </div>

            <Switch
              checked={reminderState === 'on'}
              disabled={
                reminderState === 'unsupported' || reminderState === 'blocked'
              }
              onCheckedChange={(checked) => setReminder(checked)}
              aria-label="Dagelijkse herinnering"
            />
          </div>

          {reminderState === 'blocked' && (
            <p className="text-sm text-muted-foreground">
              Meldingen staan uit. Zet ze aan in je browser- of
              telefooninstellingen om de herinnering te ontvangen.
            </p>
          )}
          {reminderState === 'unsupported' && (
            <p className="text-sm text-muted-foreground">
              Dit apparaat ondersteunt geen meldingen.
            </p>
          )}
        </div>
      )}

      {/* Selected day */}
      {selected !== 'intro' && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold">
            Dag {day.day} — {day.title}
          </h3>

          {/* Introductie */}
          <div className="space-y-3">
            <SectionHeading
              title="Introductie"
              track={day.introduction.audio}
              playing={playingId === day.introduction.audio.id}
              onPlayToggle={() => playTrack(day.introduction.audio)}
            />
            <div className="space-y-3 max-w-3xl">
              {day.introduction.body.map((paragraph, i) => (
                <p key={i} className="text-muted-foreground">
                  {paragraph.text}
                </p>
              ))}
            </div>
          </div>

          {/* Moment van Aandacht */}
          <div className="space-y-3">
            <SectionHeading
              title="Moment van Aandacht"
              track={day.moment.audio}
              playing={playingId === day.moment.audio.id}
              onPlayToggle={() => playTrack(day.moment.audio)}
            />
            <p className="text-muted-foreground max-w-3xl">
              {day.moment.prompt}
            </p>

            <div className="space-y-4 max-w-3xl">
              {day.moment.groups.map((group) => {
                const picked = group.items.filter((item) =>
                  chosen.has(item.id),
                ).length;

                return (
                  <Collapsible key={group.title} className="rounded-lg border">
                    <CollapsibleTrigger className="group flex w-full items-center gap-2 p-3 text-left">
                      <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                      <span className="text-sm font-semibold flex-1 min-w-0">
                        {group.title}
                      </span>
                      {picked > 0 && (
                        <span className="text-xs font-medium text-[#00A700] flex-shrink-0">
                          {picked} gekozen
                        </span>
                      )}
                    </CollapsibleTrigger>

                    <CollapsibleContent className="px-3 pb-3 space-y-1">
                      {group.items.map((item) => (
                        <label
                          key={item.id}
                          className="flex items-start gap-3 rounded-lg p-2 hover:bg-muted/50 cursor-pointer"
                        >
                          <Checkbox
                            checked={chosen.has(item.id)}
                            onCheckedChange={() => toggleAffirmation(item.id)}
                            className="mt-1 data-[state=checked]:bg-[#00A700] data-[state=checked]:border-[#00A700]"
                          />
                          <span className="text-muted-foreground">
                            {item.text}
                          </span>
                        </label>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>

            <Button
              variant="outline"
              disabled={chosenLines.length === 0}
              onClick={() =>
                downloadAffirmationsIcs({
                  theme: CHALLENGE.theme,
                  day: day.day,
                  lines: chosenLines,
                  time: CHALLENGE.reminderTime,
                })
              }
            >
              <CalendarPlus className="h-4 w-4 mr-2" />
              Zet in mijn agenda ({CHALLENGE.reminderTime})
            </Button>
          </div>

          {/* Meditatie */}
          <div className="space-y-3">
            <SectionHeading
              title="Meditatie"
              track={day.meditation}
              playing={playingId === day.meditation.id}
              onPlayToggle={() => playTrack(day.meditation)}
              done={dayProgress.meditation}
              onToggleDone={() => toggle(day.day, 'meditation')}
            />
          </div>
        </div>
      )}

      {/* The player, fixed at the bottom for the whole page */}
      <div
        ref={playerRef}
        className="fixed bottom-0 left-0 right-0 lg:left-64 z-50"
      >
        <AudioPlayer
          id={activeTrack.id}
          title={activeTrack.title}
          subtitle={activeTrack.subtitle}
          audioUrl={activeTrack.audioUrl}
          hideControls={['shuffle']}
          disableTracking
          minimal
          shouldPlay={shouldPlay}
          onPlayStarted={() => setShouldPlay(false)}
          shouldPause={shouldPause}
          onPlaybackStart={() => {
            setShouldPause(false);
            setPlayingId(activeTrack.id);
          }}
          onPlaybackPause={() => {
            setShouldPause(false);
            setPlayingId(null);
          }}
          className="w-full bg-black/60 backdrop-blur-md border border-white/10 shadow-lg"
        />
      </div>

      {/* Fires once, at the moment the last box is ticked */}
      <Dialog open={showCongrats} onOpenChange={setShowCongrats}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center gap-4 py-4">
            <TrophyIcon className="h-20 w-20" />
            <h3 className="text-2xl font-semibold">Gefeliciteerd!</h3>
            <p className="text-muted-foreground">
              Je hebt de {CHALLENGE.days.length}-daagse challenge{' '}
              {CHALLENGE.theme} afgerond. Je vindt hem terug op je profiel.
            </p>
            <Button onClick={() => setShowCongrats(false)}>Sluiten</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
